import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useAllCostCenters } from '~/data/costCenters';
import { EmployeeCostCenter } from '~/data/employee';
import { useAllEmployees } from '~/data/employees';
import { Enum, useAllEnums } from '~/data/enums';
import { getLegalEntityName, useAllLegalEntities } from '~/data/legalEntity';
import { useMyUnits } from '~/data/orgunits';

import { useSxStyles } from '../common/useStyles';

import { EmplHistoryType } from './data';

export type dataType = {
  data: EmplHistoryType[];
};

type EmplHistoryRow = EmplHistoryType & {
  id: string;
};

export const EmploymentHistory = (props: dataType) => {
  const { data: emplHistoryData } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { allEnums } = useAllEnums();
  const { allEmployees, getEmployeeById } = useAllEmployees();
  const { costCenters } = useAllCostCenters();
  const { legalEntities } = useAllLegalEntities();

  const { myUnits } = useMyUnits();

  const getEnumName = (enumValue: Enum[], id: string) => {
    if (id === undefined || id === '') return '';
    const specificEnum = enumValue.find((en) => en.code === id);
    return specificEnum === undefined ? id : specificEnum.name;
  };

  const orgUnit = (id: number | undefined) => {
    if (id === undefined) return '';
    const unit = myUnits.find((unit) => unit.fOrgUnitId === id);
    return unit === undefined
      ? ''
      : `${unit.fOrgUnitName} (${unit.fOrgUnitNumber})`;
  };

  const getCostCenter = (ecc: EmployeeCostCenter | undefined) => {
    if (ecc === undefined) return '';
    const cc = costCenters.find(
      (cc) => cc.fCostCenterId === ecc.fEmpCostCenter,
    );
    const ccPercent = ecc.fEmpCostCenterPercent
      ? `(${ecc.fEmpCostCenterPercent}%)`
      : '';
    return cc === undefined
      ? ''
      : `${cc.fCostCenterCode} ${cc.fCostCenterName} ${ccPercent}`.trim();
  };

  const getAdditionalCostCenter = (e: EmployeeCostCenter[] | undefined) => {
    if (e === undefined) return '';
    const cc = e.map((val) => getCostCenter(val));
    return cc
      .filter((val, index) => index > 0)
      .filter(Boolean)
      .join('\n');
  };

  const getManagerName = (e: number) => {
    return allEmployees[e]
      ? `${allEmployees[e].fEmpLastName}, ${allEmployees[e].fEmpFirstName}`
      : '';
  };

  const getEmployeeNameById = (employeeId?: number) => {
    const employee = getEmployeeById(employeeId);
    return employee
      ? `${employee.fEmpLastName}, ${employee.fEmpFirstName}`
      : '';
  };

  const rows: EmplHistoryRow[] = emplHistoryData.map((row, index) => ({
    ...row,
    id: `${row.fInternalPositionStartDate}-${row.fInternalPositionPosition}-${index}`,
  }));

  const columns: GridColDef<EmplHistoryRow>[] = [
    {
      field: 'fInternalPositionStartDate',
      headerName: t('laStartDate'),
      minWidth: 130,
      flex: 1,
      valueGetter: (_value, row) =>
        moment(row.fInternalPositionStartDate).format('DD.MM.YYYY'),
    },
    {
      field: 'fInternalPositionEndDate',
      headerName: t('laEndDate'),
      minWidth: 130,
      flex: 1,
      valueGetter: (_value, row) =>
        moment(row.fInternalPositionEndDate).format('DD.MM.YYYY'),
    },
    {
      field: 'fInternalPositionPosition',
      headerName: t('laPosition'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        getEnumName(allEnums.EMPLOYEE_POSITION, row.fInternalPositionPosition),
    },
    {
      field: 'fEmpJobTitle',
      headerName: t('laBusinessTitle'),
      minWidth: 160,
      flex: 1,
    },
    {
      field: 'fInternalPositionUnit',
      headerName: t('laOrgUnits'),
      minWidth: 180,
      flex: 1,
      valueGetter: (_value, row) => orgUnit(row.fInternalPositionUnit),
    },
    {
      field: 'fEmpCostCentersPrimary',
      headerName: t('laCC'),
      minWidth: 180,
      flex: 1,
      valueGetter: (_value, row) =>
        getCostCenter(row.fEmpCostCenters ? row.fEmpCostCenters[0] : undefined),
    },
    {
      field: 'fEmpCostCentersAdditional',
      headerName: t('laACC'),
      minWidth: 180,
      flex: 1,
      valueGetter: (_value, row) =>
        getAdditionalCostCenter(row.fEmpCostCenters),
    },
    {
      field: 'fEmpLegalEntity',
      headerName: t('laLegalEntity'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        getLegalEntityName(row.fEmpLegalEntity, legalEntities),
    },
    {
      field: 'fInternalPositionManager',
      headerName: t('laManager'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        getManagerName(row.fInternalPositionManager),
    },
    {
      field: 'fInternalPositionInfo',
      headerName: t('laInfo'),
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'fInternalPositionEnteredTime',
      headerName: t('laUpdated'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        moment(row.fInternalPositionEnteredTime).format('DD.MM.YYYY HH:mm'),
    },
    {
      field: 'fInternalPositionEnteredUser',
      headerName: t('laUpdatedBy'),
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        getEmployeeNameById(row.fInternalPositionEnteredUser),
    },
  ];

  return (
    <DataGridPremium
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      disableColumnMenu={true}
      disableRowSelectionOnClick={true}
      getRowHeight={() => 'auto'}
      initialState={{ density: 'compact' }}
      localeText={{ noRowsLabel: t('laNoHistoryNow') }}
      sx={sxStyles.dataGrid}
    />
  );
};
